import {
  APPLICATION_GENDER_OPTION_VALUES,
  JOB_POST_STATUS_OPTION_VALUES,
  UK_ETHNICITY_OPTIONS,
  UK_ROLE_TYPE_OPTION_VALUES,
  UNION_STATUS_OPTION_VALUES,
  UNION_STATUS_TYPE,
  US_ETHNICITY_OPTIONS,
  US_ROLE_TYPE_OPTION_VALUES,
} from '@lib/utils/constants/applicationConstants';
import { logIfDevelopment } from '@lib/utils/logging';
import { JobsBoardModalFilters } from '@views/jobsBoard/context';

export const buildPayloadSearchFilters = (
  jobsBoardSearchFilters: JobsBoardModalFilters
) => {
  if (!Object.keys(jobsBoardSearchFilters || {})?.length) {
    return {};
  }

  const shouldUseAgeRange = [
    jobsBoardSearchFilters?.ageRange?.min,
    jobsBoardSearchFilters?.ageRange?.max,
  ].some(Number.isInteger);

  const shouldIncludeBothNonUnionAndSAGAFTRA =
    jobsBoardSearchFilters?.unionStatuses?.includes(
      UNION_STATUS_TYPE.SAG_AFTRA
    ) || // OR is fine here
    jobsBoardSearchFilters?.unionStatuses?.includes(
      UNION_STATUS_TYPE.NON_UNION
    );

  const projectLocations =
    jobsBoardSearchFilters?.cityStateStringList?.reduce(
      (acc, cityStateString) => {
        const parsedProjectLocation =
          getParsedProjectLocationFromJsonString(cityStateString);
        if (!parsedProjectLocation) {
          return acc;
        }
        const { city, state } = parsedProjectLocation;
        acc.push({
          city,
          state,
        });
        return acc;
      },
      []
    ) || [];

  return {
    ...(projectLocations?.length
      ? {
          project_locations: projectLocations,
        }
      : {}),
    ...(jobsBoardSearchFilters?.workDates?.length
      ? {
          work_dates: jobsBoardSearchFilters?.workDates,
        }
      : {}),
    ...(jobsBoardSearchFilters?.roleTypes?.length
      ? {
          role_types: jobsBoardSearchFilters?.roleTypes,
        }
      : {}),
    ...(jobsBoardSearchFilters?.unionStatuses?.length
      ? {
          union_statuses: [
            ...jobsBoardSearchFilters?.unionStatuses,
            ...(shouldIncludeBothNonUnionAndSAGAFTRA
              ? [UNION_STATUS_TYPE.SAG_AFTRA_NON_UNION]
              : []),
          ],
        }
      : {}),
    ...(jobsBoardSearchFilters?.applicationStatuses?.length
      ? {
          application_statuses: jobsBoardSearchFilters?.applicationStatuses,
        }
      : {}),

    ...(jobsBoardSearchFilters?.genders?.length
      ? {
          genders: jobsBoardSearchFilters?.genders,
        }
      : {}),
    ...(jobsBoardSearchFilters?.ethnicities?.length
      ? {
          ethnicities: jobsBoardSearchFilters?.ethnicities,
        }
      : {}),
    ...(shouldUseAgeRange
      ? {
          age_range: {
            ...(Number.isInteger(jobsBoardSearchFilters?.ageRange?.min)
              ? { min: jobsBoardSearchFilters?.ageRange?.min }
              : {}),
            ...(Number.isInteger(jobsBoardSearchFilters?.ageRange?.max)
              ? { max: jobsBoardSearchFilters?.ageRange?.max }
              : {}),
          },
        }
      : {}),
  };
};

export type JobsBoardSearchFiltersPayload = ReturnType<
  typeof buildPayloadSearchFilters
>;

export const buildModalSearchFiltersFromBackendPayload = (
  backendPayload: JobsBoardSearchFiltersPayload,
  userCountry: ProductionCountry,
  jobsBoardLocations: LocationModel[] | undefined
): JobsBoardModalFilters => {
  if (!Object.keys(backendPayload || {})?.length) {
    return {};
  }

  const projectLocationOptions =
    buildProjectLocationOptions(jobsBoardLocations);

  const projectLocationValues = projectLocationOptions?.map(
    ({ value }) => value
  );

  const cityStateStringList = backendPayload?.project_locations?.reduce(
    (acc, projectLocation) => {
      const { city, state } = projectLocation || {};
      const normalizedCity = city?.trim() || null;
      const normalizedState = state?.trim() || null;

      if (![normalizedCity, normalizedState].some(Boolean)) {
        return acc;
      }

      const cityStateString = JSON.stringify({
        city: normalizedCity,
        state: normalizedState,
      });

      if (projectLocationValues?.includes(cityStateString)) {
        acc.push(cityStateString);
      }

      return acc;
    },
    [] as string[]
  );

  const roleTypeOptions =
    userCountry === 'UK'
      ? UK_ROLE_TYPE_OPTION_VALUES
      : US_ROLE_TYPE_OPTION_VALUES;

  const ethnicityOptions =
    userCountry === 'UK' ? UK_ETHNICITY_OPTIONS : US_ETHNICITY_OPTIONS;

  const normalizedRoleTypes =
    (roleTypeOptions as string[])?.filter((roleType) =>
      backendPayload?.role_types?.includes(roleType)
    ) || [];

  const normalizedApplicationStatuses =
    JOB_POST_STATUS_OPTION_VALUES?.filter((applicationStatus) =>
      (backendPayload?.application_statuses as string[])?.includes(
        applicationStatus
      )
    ) || [];

  const normalizedGenders =
    APPLICATION_GENDER_OPTION_VALUES?.filter((gender) =>
      backendPayload?.genders?.includes(gender)
    ) || [];

  const normalizedEthnicities =
    (ethnicityOptions as string[])?.filter((ethnicity) =>
      backendPayload?.ethnicities?.includes(ethnicity)
    ) || [];

  const normalizedUnionStatuses =
    userCountry === 'UK'
      ? []
      : UNION_STATUS_OPTION_VALUES?.filter((unionStatus) =>
          backendPayload?.union_statuses?.includes(unionStatus)
        ) || [];

  const shouldUseAgeRange = [
    backendPayload?.age_range?.min,
    backendPayload?.age_range?.max,
  ].some(Number.isInteger);

  return {
    ...(cityStateStringList?.length
      ? {
          cityStateStringList,
        }
      : {}),
    ...(backendPayload?.work_dates?.length
      ? {
          workDates: backendPayload?.work_dates,
        }
      : {}),
    ...(normalizedRoleTypes?.length
      ? {
          roleTypes: normalizedRoleTypes,
        }
      : {}),
    ...(normalizedApplicationStatuses?.length
      ? {
          applicationStatuses: normalizedApplicationStatuses,
        }
      : {}),
    ...(normalizedGenders?.length
      ? {
          genders: normalizedGenders,
        }
      : {}),
    ...(normalizedEthnicities?.length
      ? {
          ethnicities: normalizedEthnicities,
        }
      : {}),
    ...(normalizedUnionStatuses?.length
      ? {
          unionStatuses: normalizedUnionStatuses,
        }
      : {}),
    ...(shouldUseAgeRange
      ? {
          ageRange: {
            ...(Number.isInteger(backendPayload?.age_range?.min)
              ? { min: backendPayload?.age_range?.min }
              : {}),
            ...(Number.isInteger(backendPayload?.age_range?.max)
              ? { max: backendPayload?.age_range?.max }
              : {}),
          },
        }
      : {}),
  };
};

export const JOBS_BOARD_POSTS_PUBLIC_QUERY_KEY = 'jobs-board-posts-public';
export const JOBS_BOARD_POSTS_PRIVATE_QUERY_KEY = 'jobs-board-posts-private';

export const getParsedProjectLocationFromJsonString = (
  cityStateString: string | null | undefined
) => {
  if (!cityStateString) {
    return null;
  }

  try {
    const { city, state } = JSON.parse(cityStateString);
    const normalizedCity = city?.trim() || null;
    const normalizedState = state?.trim() || null;

    if (![normalizedCity, normalizedState].some(Boolean)) {
      return null;
    }

    return {
      city: normalizedCity,
      state: normalizedState,
    };
  } catch (error) {
    logIfDevelopment(error);
    return null;
  }
};

export const buildProjectLocationOptions = (
  projectLocations: LocationModel[] | undefined
) => {
  const rawList =
    projectLocations?.reduce((acc, { city, state }) => {
      const normalizedCity = city?.trim() || null;
      const normalizedState = state?.trim() || null;
      const cityState = [normalizedCity, normalizedState]
        .filter(Boolean)
        .join(', ');

      if (!cityState) {
        return acc;
      }

      const value = JSON.stringify({
        city: normalizedCity,
        state: normalizedState,
      });

      acc.push({
        label: cityState,
        value,
      });

      return acc;
    }, [] as SelectOption[]) || ([] as SelectOption[]);

  const uniqueOptionsByLabel = Array.from(
    new Map(rawList?.map((item) => [item?.label, item]) || []).values()
  );
  return uniqueOptionsByLabel;
};

export type SelectOption = {
  label: string;
  value: string;
};
